import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useState} from 'react';

import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';

import {getLabcodeApiBaseUrl, studentLogin} from '../../lib/labcode-api';
import {setLabcodeStudentSession} from '../../lib/labcode-student-session';

import styles from './student-login.css';

/**
 * Login estudante: código da turma + apelido (cadastrados no servidor).
 * Visual alinhado ao ecossistema LabCriar (fundo em gradiente, cartão branco, CTA em destaque).
 */
const StudentLogin = ({open, onClose, gateFullscreen, embedded, onLoginSuccess}) => {
    const showGateCopy = gateFullscreen || embedded;
    const [classCode, setClassCode] = useState('');
    const [nickname, setNickname] = useState('');
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(false);

    const handleClassCodeChange = useCallback(e => {
        setClassCode(e.target.value.toUpperCase());
    }, []);

    const handleNicknameChange = useCallback(e => {
        setNickname(e.target.value);
    }, []);

    useEffect(() => {
        if (!open && !gateFullscreen && !embedded) {
            setClassCode('');
            setNickname('');
            setError(null);
        }
    }, [open, gateFullscreen, embedded]);

    const handleClose = useCallback(() => {
        if (gateFullscreen || embedded) return;
        setError(null);
        onClose();
    }, [gateFullscreen, embedded, onClose]);

    const handleSubmit = useCallback(async e => {
        e.preventDefault();
        setError(null);
        setPending(true);
        try {
            const data = await studentLogin(classCode, nickname);
            const saved = setLabcodeStudentSession(data.token, data.student);
            if (!saved) {
                setError(
                    'Não foi possível guardar a sessão neste navegador. Desative modo privado ou bloqueio a cookies/armazenamento.'
                );
                return;
            }
            if (typeof onLoginSuccess === 'function') {
                onLoginSuccess(data);
            }
            if (!gateFullscreen) {
                setClassCode('');
                setNickname('');
                onClose();
            } else {
                setClassCode('');
                setNickname('');
            }
        } catch (err) {
            setError(err && err.message ? err.message : 'Erro ao entrar.');
        } finally {
            setPending(false);
        }
    }, [classCode, nickname, gateFullscreen, onClose, onLoginSuccess]);

    const formScratch = (
        <form
            className={styles.scratchForm}
            id="student-login-form"
            onSubmit={handleSubmit}
        >
            <div className={styles.cardBody}>
                {showGateCopy ? (
                    <>
                        <h1
                            className={styles.title}
                            id="student-login-title"
                        >
                            {embedded ? 'Entrar como aluno' : 'Entrar'}
                        </h1>
                        <p className={styles.subtitle}>
                            {
                                'Entre na turma com o código e o apelido que o professor cadastrou. Depois o editor abre.'
                            }
                        </p>
                    </>
                ) : (
                    <p className={styles.subtitle}>
                        {
                            'Use o código da turma e o teu apelido cadastrado.'
                        }
                    </p>
                )}

                {gateFullscreen ? (
                    <p className={styles.supportHint}>
                        {
                            'Se não avançar: confirme o labcode-server (API) e REACT_APP_LABCODE_API_URL no .env. Emergência: REACT_APP_LABCODE_AUTH_DISABLE=1 e npm start.'
                        }
                    </p>
                ) : null}
                {embedded ? (
                    <p className={styles.supportHint}>
                        {
                            'Alunos: após entrar, abre-se o editor Labcode. Equipa escolar: use o separador “Equipa escolar”.'
                        }
                    </p>
                ) : null}
                {!gateFullscreen && !embedded && !getLabcodeApiBaseUrl() ? (
                    <p className={styles.supportHint}>
                        {'Configure REACT_APP_LABCODE_API_URL no .env e reinicie o npm start.'}
                    </p>
                ) : null}

                {error ? (
                    <div
                        aria-live="polite"
                        className={styles.errorBanner}
                        role="alert"
                    >
                        {error}
                    </div>
                ) : null}

                <div className={styles.fieldGroup}>
                    <label
                        className={styles.fieldLabel}
                        htmlFor="student-login-class-code"
                    >
                        {'Código da turma'}
                    </label>
                    <div className={styles.fieldRow}>
                        <input
                            autoCapitalize="characters"
                            autoComplete="off"
                            className={styles.scratchInput}
                            disabled={pending}
                            id="student-login-class-code"
                            maxLength={12}
                            placeholder="Ex.: DEMO01"
                            type="text"
                            value={classCode}
                            onChange={handleClassCodeChange}
                        />
                        <aside className={styles.tipBubble}>
                            {'Use exatamente o código que o professor divulgou (letras e números).'}
                        </aside>
                    </div>
                </div>

                <div className={styles.fieldGroup}>
                    <label
                        className={styles.fieldLabel}
                        htmlFor="student-login-nickname"
                    >
                        {'Apelido na turma'}
                    </label>
                    <div className={styles.fieldRow}>
                        <input
                            autoComplete="nickname"
                            className={styles.scratchInput}
                            disabled={pending}
                            id="student-login-nickname"
                            maxLength={64}
                            placeholder="Seu apelido"
                            type="text"
                            value={nickname}
                            onChange={handleNicknameChange}
                        />
                        <aside className={styles.tipBubble}>
                            {'Não precisa ser o teu nome verdadeiro — só o apelido registado na turma.'}
                        </aside>
                    </div>
                </div>

                {!gateFullscreen ? (
                    <button
                        className={styles.btnGhost}
                        disabled={pending}
                        type="button"
                        onClick={handleClose}
                    >
                        {'Cancelar'}
                    </button>
                ) : null}
            </div>

            <div className={styles.cardFooter}>
                <button
                    className={styles.footerSubmit}
                    disabled={pending}
                    type="submit"
                >
                    {pending ? 'Entrando…' : 'Entrar'}
                </button>
            </div>
        </form>
    );

    if (embedded) {
        return (
            <div className={styles.hubEmbeddedCard}>
                {formScratch}
            </div>
        );
    }

    if (gateFullscreen) {
        return (
            <div
                aria-labelledby="student-login-title"
                aria-modal="true"
                className={styles.gateOverlay}
                role="dialog"
            >
                <div
                    aria-hidden="true"
                    className={styles.brandMark}
                >
                    {'Labcode'}
                </div>
                <div className={styles.gateCard}>
                    {formScratch}
                </div>
            </div>
        );
    }

    if (!open) {
        return null;
    }

    return (
        <Modal
            className={styles.modalShell}
            closeButtonVisible
            contentLabel="Entrar na turma"
            headerClassName={styles.scratchModalHeader}
            id="studentLoginModal"
            onRequestClose={handleClose}
        >
            <Box
                className={styles.modalBodyWrap}
                direction="column"
            >
                {formScratch}
            </Box>
        </Modal>
    );
};

StudentLogin.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    gateFullscreen: PropTypes.bool,
    embedded: PropTypes.bool,
    onLoginSuccess: PropTypes.func
};

StudentLogin.defaultProps = {
    open: false,
    onClose: () => {},
    gateFullscreen: false,
    embedded: false,
    onLoginSuccess: null
};

export default StudentLogin;
